import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { ROUTES } from '../../api/routes';
import { getDiaSemanaFromDate } from '../../api/hooks/useHorarios';
import {
  chamadaCobreSlot,
  classificarSlotChamadaOperacional,
  normalizarLabelTurno,
  resolverFaseTurnosPorRelogio,
  toMinutes,
  type SlotChamadaStatusOperacional,
} from '../../components/chamada';
import type { HorarioAula, Turma } from '../../types/models';

export type TurnoAoVivoSlotCell = {
  horarioId: string;
  numeroAula: number;
  horaInicio: string;
  horaFim: string;
  professorNome: string | null;
  materiaNome: string | null;
  status: SlotChamadaStatusOperacional;
  title: string;
};

export type TurnoAoVivoTurmaRow = {
  turmaId: string;
  turmaNome: string;
  turnoLabel: string;
  cells: Map<number, TurnoAoVivoSlotCell>;
  perdidas: number;
  ok: number;
  semNenhuma: boolean;
  parcial: boolean;
};

export type TurnoAoVivoKpis = {
  previstas: number;
  ok: number;
  emJanela: number;
  emCurso: number;
  perdidas: number;
  futuras: number;
  aderenciaPct: number | null;
  /** Turmas do turno sem nenhuma chamada e com ≥1 slot perdido. */
  turmasSemChamada: number;
  /** Turmas com alguma chamada e ≥1 slot perdido. */
  turmasParciais: number;
  turmasComRegistro: number;
  turmasAguardando: number;
  turmasNoTurno: number;
};

export type TurnoAoVivoModel = {
  isLoading: boolean;
  turnoLabel: string | null;
  aberturaHm: string | null;
  fechamentoHm: string | null;
  agoraPct: number;
  numerosAula: number[];
  rows: TurnoAoVivoTurmaRow[];
  kpis: TurnoAoVivoKpis;
};

function sliceHm(v?: string | null): string {
  return String(v || '').slice(0, 5);
}

function pctNaJanela(agora: string, ini: string, fim: string): number {
  const a = toMinutes(agora);
  const i = toMinutes(ini);
  const f = toMinutes(fim);
  if (f <= i) return 0;
  if (a <= i) return 0;
  if (a >= f) return 100;
  return Math.round(((a - i) / (f - i)) * 100);
}

const EMPTY_KPIS: TurnoAoVivoKpis = {
  previstas: 0,
  ok: 0,
  emJanela: 0,
  emCurso: 0,
  perdidas: 0,
  futuras: 0,
  aderenciaPct: null,
  turmasSemChamada: 0,
  turmasParciais: 0,
  turmasComRegistro: 0,
  turmasAguardando: 0,
  turmasNoTurno: 0,
};

/**
 * Dados do painel “Turno ao vivo”: grade do dia × status-dia × relógio,
 * filtrado pelo turno vigente (ou label forçado).
 */
export function useTurnoAoVivo(opts: {
  escolaId?: string | null;
  hojeStr: string;
  horaAtual: string;
  turmas: Turma[];
  enabled?: boolean;
  /** Se omitido, usa o turno com fase "agora" no relógio. */
  turnoLabelForce?: string | null;
  pollMs?: number | false;
}): TurnoAoVivoModel {
  const {
    escolaId,
    hojeStr,
    horaAtual,
    turmas,
    enabled = true,
    turnoLabelForce,
    pollMs = 15_000,
  } = opts;

  const dayReady = /^\d{4}-\d{2}-\d{2}$/.test(hojeStr);
  const diaSemana = dayReady ? getDiaSemanaFromDate(hojeStr) : null;

  const { data: horarios = [], isLoading: loadingH } = useQuery({
    queryKey: ['turno-ao-vivo-horarios', escolaId, diaSemana, hojeStr],
    queryFn: async () => {
      const { data } = await api.get(ROUTES.horarios.porEscola(escolaId!), {
        params: { diaSemana, data: hojeStr },
      });
      const list = (Array.isArray(data) ? data : data?.data ?? []) as HorarioAula[];
      return list.filter((h) => String(h.tipo || 'AULA').toUpperCase() === 'AULA');
    },
    enabled: !!escolaId && !!diaSemana && dayReady && enabled,
    staleTime: 30_000,
    refetchInterval: pollMs,
  });

  const { data: statusDia = [], isLoading: loadingC } = useQuery({
    queryKey: ['chamadas-status-dia', escolaId, hojeStr],
    queryFn: async () => {
      const { data } = await api.get(ROUTES.chamadas.statusDia(escolaId!), {
        params: { data: hojeStr },
      });
      return Array.isArray(data) ? data : [];
    },
    enabled: !!escolaId && dayReady && enabled,
    staleTime: 15_000,
    refetchInterval: pollMs,
  });

  const fasePorLabel = useMemo(
    () => resolverFaseTurnosPorRelogio(horaAtual, turmas || []),
    [horaAtual, turmas],
  );

  const turnoLabel = useMemo(() => {
    if (turnoLabelForce) return turnoLabelForce;
    const ativo = [...fasePorLabel.entries()].find(([, f]) => f === 'agora')?.[0];
    if (ativo) return ativo;
    // Fora da janela: preferir o turno cujo fechamento ainda não passou e abertura já passou
    for (const label of ['Manhã', 'Tarde', 'Noite'] as const) {
      if (fasePorLabel.get(label) === 'passado') continue;
      if (fasePorLabel.has(label)) return label;
    }
    return [...fasePorLabel.keys()][0] ?? null;
  }, [turnoLabelForce, fasePorLabel]);

  const bounds = useMemo(() => {
    if (!turnoLabel) return { abertura: null as string | null, fechamento: null as string | null };
    const t = turmas.find(
      (x) =>
        normalizarLabelTurno(x.turnoOperacional?.nome || x.turno) === turnoLabel,
    );
    const op = t?.turnoOperacional;
    const ab = sliceHm(op?.horaAberturaPortoes);
    const fc = sliceHm(op?.horaFechamentoPortoes || op?.horaSaidaOficial);
    return {
      abertura: /^\d{2}:\d{2}$/.test(ab) ? ab : null,
      fechamento: /^\d{2}:\d{2}$/.test(fc) ? fc : null,
    };
  }, [turnoLabel, turmas]);

  const model = useMemo((): Omit<TurnoAoVivoModel, 'isLoading'> => {
    if (!turnoLabel) {
      return {
        turnoLabel: null,
        aberturaHm: null,
        fechamentoHm: null,
        agoraPct: 0,
        numerosAula: [],
        rows: [],
        kpis: EMPTY_KPIS,
      };
    }

    const turmaById = new Map(turmas.map((t) => [t.id, t]));
    const horariosTurno = horarios.filter((h) => {
      const t = h.turmaId ? turmaById.get(h.turmaId) : undefined;
      const label = normalizarLabelTurno(
        (h as any).turma?.turnoOperacional?.nome ||
          (h as any).turma?.turno ||
          t?.turnoOperacional?.nome ||
          t?.turno,
      );
      return label === turnoLabel;
    });

    const numeros = [
      ...new Set(
        horariosTurno
          .map((h) => Number(h.numeroAula) || 0)
          .filter((n) => n > 0),
      ),
    ].sort((a, b) => a - b);

    const byTurma = new Map<string, HorarioAula[]>();
    for (const h of horariosTurno) {
      if (!h.turmaId) continue;
      const list = byTurma.get(h.turmaId) || [];
      list.push(h);
      byTurma.set(h.turmaId, list);
    }

    let previstas = 0;
    let ok = 0;
    let emJanela = 0;
    let emCurso = 0;
    let perdidas = 0;
    let futuras = 0;
    let turmasSemChamada = 0;
    let turmasParciais = 0;
    let turmasComRegistro = 0;
    let turmasAguardando = 0;

    const rows: TurnoAoVivoTurmaRow[] = [];

    for (const [turmaId, slots] of byTurma) {
      const t = turmaById.get(turmaId);
      const cells = new Map<number, TurnoAoVivoSlotCell>();
      let rowPerdidas = 0;
      let rowOk = 0;

      for (const h of slots) {
        const n = Number(h.numeroAula) || 0;
        if (n <= 0) continue;
        const match = statusDia.find((c: any) => chamadaCobreSlot(c, h));
        const status = classificarSlotChamadaOperacional(h, match, horaAtual);
        previstas += 1;
        if (status === 'registrada') {
          ok += 1;
          rowOk += 1;
        } else if (status === 'emCurso') emCurso += 1;
        else if (status === 'janela') emJanela += 1;
        else if (status === 'perdida') {
          perdidas += 1;
          rowPerdidas += 1;
        } else futuras += 1;

        const ini = sliceHm(h.horaInicio);
        const fim = sliceHm(h.horaFim);
        const prof =
          (h as any).professor?.nome ||
          (match as any)?.professorNome ||
          null;
        const mat =
          (h as any).materia?.nome ||
          (match as any)?.materiaNome ||
          null;
        cells.set(n, {
          horarioId: h.id,
          numeroAula: n,
          horaInicio: ini,
          horaFim: fim,
          professorNome: prof,
          materiaNome: mat,
          status,
          title: [
            `${n}ª aula`,
            ini && fim ? `${ini}–${fim}` : null,
            mat,
            prof,
            status,
          ]
            .filter(Boolean)
            .join(' · '),
        });
      }

      const semNenhuma = rowOk === 0 && rowPerdidas > 0;
      const parcial = rowOk > 0 && rowPerdidas > 0;
      if (semNenhuma) turmasSemChamada += 1;
      else if (parcial) turmasParciais += 1;
      if (rowOk > 0) turmasComRegistro += 1;
      if (rowOk === 0 && rowPerdidas === 0 && slots.length > 0) {
        turmasAguardando += 1;
      }

      rows.push({
        turmaId,
        turmaNome: t?.nome || (slots[0] as any)?.turma?.nome || 'Turma',
        turnoLabel,
        cells,
        perdidas: rowPerdidas,
        ok: rowOk,
        semNenhuma,
        parcial,
      });
    }

    rows.sort((a, b) => {
      const rank = (r: TurnoAoVivoTurmaRow) =>
        r.semNenhuma ? 0 : r.parcial ? 1 : r.ok > 0 ? 2 : 3;
      const dr = rank(a) - rank(b);
      if (dr !== 0) return dr;
      return a.turmaNome.localeCompare(b.turmaNome, 'pt-BR');
    });

    const encerraveis = ok + perdidas;
    const aderenciaPct =
      encerraveis > 0 ? Math.round((ok / encerraveis) * 100) : null;

    const aberturaHm = bounds.abertura;
    const fechamentoHm = bounds.fechamento;
    // Fallback: 1º início → último fim dos slots do turno
    let ab = aberturaHm;
    let fc = fechamentoHm;
    if (!ab || !fc) {
      const inis = horariosTurno.map((h) => sliceHm(h.horaInicio)).filter(Boolean);
      const fins = horariosTurno.map((h) => sliceHm(h.horaFim)).filter(Boolean);
      if (!ab && inis.length) ab = inis.sort()[0];
      if (!fc && fins.length) fc = fins.sort().slice(-1)[0];
    }

    return {
      turnoLabel,
      aberturaHm: ab,
      fechamentoHm: fc,
      agoraPct: ab && fc ? pctNaJanela(horaAtual, ab, fc) : 0,
      numerosAula: numeros,
      rows,
      kpis: {
        previstas,
        ok,
        emJanela,
        emCurso,
        perdidas,
        futuras,
        aderenciaPct,
        turmasSemChamada,
        turmasParciais,
        turmasComRegistro,
        turmasAguardando,
        turmasNoTurno: rows.length,
      },
    };
  }, [
    turnoLabel,
    turmas,
    horarios,
    statusDia,
    horaAtual,
    bounds.abertura,
    bounds.fechamento,
  ]);

  return {
    ...model,
    isLoading: loadingH || loadingC,
  };
}
